import { useState } from "react";
import { Link2, Unlink } from "lucide-react";
import { trpc } from "../../hooks/trpc";

// =====================================================================
// Admin-Panel · Nutzer und Personen (Migration 0049, nur GF).
//
// Jeder Hub-Nutzer kann genau einer Person zugeordnet werden (intern aus
// stammdaten_mitarbeiter oder extern aus stammdaten_externe). Die Zuordnung
// liefert den Claim personUuid an die Satelliten. Der Server erzwingt GF
// (personLink-Router, gfProcedure); diese Ansicht ist nur Bedienung.
// =====================================================================

const STROKE = 1.6;

function personLabel(p: { vorname: string | null; nachname: string | null }): string {
  return [p.nachname, p.vorname].filter(Boolean).join(", ") || "Ohne Namen";
}

export function PersonLinkView() {
  const utils = trpc.useUtils();
  const listQuery = trpc.personLink.list.useQuery();
  const [selected, setSelected] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const searchQuery = trpc.personLink.search.useQuery(
    { search },
    { enabled: selected !== null && search.trim().length >= 2 },
  );
  const refresh = () => {
    utils.personLink.list.invalidate();
    utils.personLink.search.invalidate();
  };
  const linkMutation = trpc.personLink.link.useMutation({
    onSuccess: () => { refresh(); setSelected(null); setSearch(""); },
    onError: (err) => alert(`Fehler: ${err.message}`),
  });
  const unlinkMutation = trpc.personLink.unlink.useMutation({
    onSuccess: refresh,
    onError: (err) => alert(`Fehler: ${err.message}`),
  });

  if (listQuery.isLoading) {
    return <div className="cc-card"><div className="cc-card-body">Lade Nutzer…</div></div>;
  }
  if (listQuery.error) {
    return (
      <div className="cc-card" data-testid="admin-person-link-error">
        <div className="cc-card-body">
          <span className="cc-badge-st is-bad">Fehler</span>
          <span style={{ marginLeft: 8 }}>{listQuery.error.message}</span>
        </div>
      </div>
    );
  }

  const rows = listQuery.data ?? [];
  const selectedUser = rows.find((u) => u.userId === selected) ?? null;
  const verknuepft = rows.filter((u) => u.person).length;

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="admin-person-link">
      <div className="cc-card">
        <div className="cc-card-head">
          <span className="cc-card-title">Nutzer und Personen</span>
          <span className="cc-pill">{verknuepft} von {rows.length} verknuepft</span>
        </div>
        <div className="cc-card-body is-flush">
          <div className="cc-list">
            {rows.map((u) => (
              <div
                key={u.userId}
                className="cc-list-row"
                style={{
                  gridTemplateColumns: "1.3fr 110px 1.6fr 150px",
                  opacity: u.isActive === false ? 0.55 : 1,
                  background: selected === u.userId ? "var(--surface-2)" : undefined,
                }}
                data-testid={`admin-person-link-user-${u.userId}`}
              >
                <span>{u.name ?? u.username} <span className="cc-row-label">@{u.username}</span></span>
                <span className="cc-badge-st is-info">{u.roleName ?? "—"}</span>
                {u.person ? (
                  <span>
                    {personLabel(u.person)}{" "}
                    <span className="cc-row-label">
                      {u.person.personTyp === "intern" ? "intern" : "extern"} · {u.person.mandantId ?? "ohne Mandant"}
                      {u.person.status && u.person.status !== "aktiv" ? ` · ${u.person.status}` : ""}
                    </span>
                  </span>
                ) : (
                  <span className="cc-row-label">
                    nicht verknuepft
                    {u.emailTreffer === 1 ? " · 1 Person mit gleicher E-Mail" : ""}
                    {u.emailTreffer > 1 ? ` · ${u.emailTreffer} Personen mit gleicher E-Mail (mehrdeutig)` : ""}
                  </span>
                )}
                <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="cc-btn"
                    onClick={() => { setSelected(u.userId); setSearch(""); }}
                    data-testid={`admin-person-link-select-${u.userId}`}
                  >
                    <Link2 size={13} strokeWidth={STROKE} /> {u.person ? "Aendern" : "Verknuepfen"}
                  </button>
                  {u.person ? (
                    <button
                      type="button"
                      className="cc-btn"
                      disabled={unlinkMutation.isPending}
                      onClick={() => {
                        if (confirm(`Verknuepfung von "${u.username}" loesen? Neue App-Starts tragen dann keinen oder nur den E-Mail-Claim.`)) {
                          unlinkMutation.mutate({ userId: u.userId });
                        }
                      }}
                      data-testid={`admin-person-unlink-${u.userId}`}
                    >
                      <Unlink size={13} strokeWidth={STROKE} /> Loesen
                    </button>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedUser ? (
        <div className="cc-card" data-testid="admin-person-link-search">
          <div className="cc-card-head">
            <span className="cc-card-title">Person fuer {selectedUser.name ?? selectedUser.username} waehlen</span>
            <button type="button" className="cc-btn" onClick={() => setSelected(null)}>Abbrechen</button>
          </div>
          <div className="cc-card-body">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name oder E-Mail (mind. 2 Zeichen)"
              aria-label="Person suchen"
              style={{ width: "100%", marginBottom: 12 }}
              data-testid="admin-person-link-input"
            />
            <div className="cc-list">
              {(searchQuery.data ?? []).map((p) => {
                const vergeben = p.linkedUserId !== null && p.linkedUserId !== selectedUser.userId;
                return (
                  <div
                    key={`${p.personTyp}-${p.personId}`}
                    className="cc-list-row"
                    style={{ gridTemplateColumns: "1.4fr 1.4fr 150px 130px" }}
                  >
                    <span>
                      {personLabel(p)}{" "}
                      <span className="cc-row-label">{p.personTyp} · {p.mandantId ?? "ohne Mandant"} · {p.status ?? "—"}</span>
                    </span>
                    <span className="cc-row-label">{p.email ?? "ohne E-Mail"}</span>
                    <span className="cc-row-label">{p.linkedUsername ? `zugeordnet: @${p.linkedUsername}` : "frei"}</span>
                    <button
                      type="button"
                      className="cc-btn"
                      disabled={vergeben || linkMutation.isPending}
                      title={vergeben ? "Bereits einem anderen Nutzer zugeordnet" : undefined}
                      onClick={() => linkMutation.mutate({ userId: selectedUser.userId, personTyp: p.personTyp, personId: p.personId })}
                    >
                      Zuordnen
                    </button>
                  </div>
                );
              })}
              {searchQuery.data && searchQuery.data.length === 0 ? (
                <div className="cc-list-row"><span style={{ color: "var(--text-3)" }}>Keine Person gefunden.</span></div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
